import { OrganizationSetupWizard, type WizardData } from "@repo/auth"
import { authClient } from "@repo/db"
import type { Doc, Id } from "@repo/db"
import { api } from "@repo/db/convex/_generated/api"
import { BrandMark, Wallpaper } from "@repo/ui"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { useMutation } from "convex/react"
import { Loader2 } from "lucide-react"
import { useCallback, useEffect, useRef } from "react"

import { useConvex } from "@/hooks/use-convex"
import { convexClient, queryWithTimeout } from "@/lib/convex"

export const Route = createFileRoute("/create-dealership")({
  beforeLoad: async (ctx) => {
    const session = await authClient.getSession()
    if (!session || !session.data) {
      throw redirect({ to: "/login", search: { redirect: ctx.location.pathname } })
    }

    const user = await queryWithTimeout(convexClient.query(api.auth.getCurrentUser))
    if (!user) {
      throw redirect({ to: "/login", search: { redirect: ctx.location.pathname } })
    }
    if (!user.name || !user.email) {
      throw redirect({ to: "/complete-profile" })
    }
    if (user.isDemo) {
      if (user.dealership) {
        throw redirect({
          to: "/$dealership/dashboard",
          params: { dealership: user.dealership.slug },
        })
      }
      throw redirect({ to: "/demo" })
    }
  },
  component: RouteComponent,
})

type OnboardingState = {
  dealership: Doc<"dealerships">
  stores: Doc<"stores">[]
  betterAuthOrgId: string
}

function computeResumeData(state: OnboardingState): {
  initialData: Partial<WizardData>
  initialStep: number
} {
  const { dealership, stores } = state
  const extraction = dealership.extractionMetadata as Record<string, unknown> | undefined
  const questionnaire = dealership.questionnaireData

  const initialData: Partial<WizardData> = {
    dealershipId: dealership._id,
    betterAuthOrgId: state.betterAuthOrgId,
    organizationName: dealership.name,
    slug: dealership.slug,
    website: dealership.website,
    dealershipType: dealership.dealershipType,
    primaryColor: dealership.primaryColor ?? "",
    secondaryColor: dealership.secondaryColor,
    accentColor: dealership.accentColor,
    description: dealership.description,
    slogan: dealership.slogan,
    logo: dealership.logoUrl,
    socialLinks: dealership.socialLinks,
    ownerContact: dealership.ownerContact,
    stores: stores.map((s) => ({
      name: s.name,
      domain: s.domain,
      address: s.address,
      phone: s.phone,
      email: s.email,
      inventoryUrl: s.inventoryUrl,
    })),
    acceptedTerms: false,
  }

  // Reconstruct extractedData from extractionMetadata if present
  if (extraction) {
    // Cast needed: schema stores extractionMetadata as v.any(), losing type info on read
    initialData.extractedData = extraction as unknown as WizardData["extractedData"]
    initialData.extractionComplete = true
  }

  // Reconstruct questionnaire fields
  if (questionnaire) {
    initialData.salesFocus = questionnaire.salesFocus
    initialData.biggestHeadache = questionnaire.biggestHeadache
    initialData.inventoryManagement = questionnaire.inventoryManagement
    initialData.dmsSystem = questionnaire.dmsSystem
    initialData.websiteImportance = questionnaire.websiteImportance
    initialData.moneyLeftOnTable = questionnaire.moneyLeftOnTable
    initialData.mustHaveIntegrations = questionnaire.mustHaveIntegrations
    initialData.monthlyVolume = questionnaire.monthlyVolume
  }

  // Compute step based on data completeness
  let initialStep = 0
  if (!extraction) {
    initialStep = 1 // WebsiteUrl
  } else if (!questionnaire) {
    initialStep = 2 // Questionnaire
  } else if (!dealership.ownerContact) {
    initialStep = 4 // DealershipConfig (skip extraction)
  } else if (stores.length === 0) {
    initialStep = 5 // StoreLocations
  } else {
    initialStep = 6 // Review
  }

  return { initialData, initialStep }
}

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { data: onboardingState } = useConvex(api.dealerships.queries.getOnboardingState, {})
  const orgActivatedRef = useRef(false)

  // Set Better Auth active org on resume (fire-and-forget)
  useEffect(() => {
    if (onboardingState && !orgActivatedRef.current) {
      orgActivatedRef.current = true
      void authClient.organization.setActive({ organizationId: onboardingState.betterAuthOrgId })
    }
  }, [onboardingState])

  // Create Better Auth organization
  const createBetterAuthOrg = async (data: {
    organizationName: string
    slug: string
    metadata?: Record<string, unknown>
  }) => {
    const orgResult = await authClient.organization.create({
      name: data.organizationName,
      slug: data.slug,
    })

    if (!orgResult.data?.id) {
      throw new Error("Failed to create Better Auth organization")
    }

    return orgResult.data.id
  }
  const createDealership = useMutation(api.dealerships.mutations.createFromExtraction)
  const updateDealership = useMutation(api.dealerships.mutations.update)
  const updateWithExtractionData = useMutation(api.dealerships.mutations.updateWithExtractionData)
  const updateQuestionnaireData = useMutation(api.dealerships.mutations.updateQuestionnaireData)
  const createStore = useMutation(api.stores.mutations.createStore)
  const ensureMembership = useMutation(api.dealerships.mutations.ensureMembership)
  const setActiveDealership = useMutation(api.users.mutations.setActiveDealership)
  const completeOnboarding = useMutation(api.dealerships.mutations.completeOnboarding)
  const initNotificationPrefs = useMutation(api.users.notificationPreferences.initDefaults)

  const resumeData = onboardingState ? computeResumeData(onboardingState) : null
  const isResuming = !!resumeData && resumeData.initialStep > 0

  const handlePersistStep = useCallback(
    (stepId: string, data: Partial<WizardData>) => {
      const dealershipId = data.dealershipId
      if (!dealershipId) return

      if (stepId === "questionnaire") {
        updateQuestionnaireData({
          dealershipId: dealershipId as Id<"dealerships">,
          questionnaireData: {
            salesFocus: data.salesFocus || undefined,
            biggestHeadache: data.biggestHeadache || undefined,
            inventoryManagement: data.inventoryManagement || undefined,
            dmsSystem: data.dmsSystem || undefined,
            websiteImportance: data.websiteImportance || undefined,
            moneyLeftOnTable: data.moneyLeftOnTable?.length ? data.moneyLeftOnTable : undefined,
            mustHaveIntegrations: data.mustHaveIntegrations?.length
              ? data.mustHaveIntegrations
              : undefined,
            monthlyVolume: data.monthlyVolume || undefined,
          },
        }).catch((e) => console.error("[persist] questionnaire failed:", e))
      }

      if (stepId === "dealership") {
        updateDealership({
          dealershipId: dealershipId as Id<"dealerships">,
          ownerContact: data.ownerContact,
          dealershipType: data.dealershipType,
          name: data.organizationName,
          primaryColor: data.primaryColor,
          secondaryColor: data.secondaryColor,
          accentColor: data.accentColor,
        }).catch((e) => console.error("[persist] dealership update failed:", e))

        if (data.extractedData) {
          updateWithExtractionData({
            dealershipId: dealershipId as Id<"dealerships">,
            extractionData: data.extractedData,
          }).catch((e) => console.error("[persist] extraction data failed:", e))
        }
      }
    },
    [updateQuestionnaireData, updateDealership, updateWithExtractionData],
  )

  const handleWizardComplete = async (data: WizardData) => {
    try {
      let orgId = data.betterAuthOrgId
      let dealershipId = data.dealershipId

      if (!orgId) {
        orgId = await createBetterAuthOrg({
          organizationName: data.organizationName,
          slug: data.slug,
        })
      }

      // If we have extraction data and a dealership was already created, update it
      if (data.extractedData && dealershipId) {
        await updateWithExtractionData({
          dealershipId: dealershipId as Id<"dealerships">,
          extractionData: data.extractedData,
        })
      } else if (data.extractedData && !dealershipId) {
        dealershipId = await createDealership({
          betterAuthOrgId: orgId!,
          extractionData: data.extractedData,
          website: data.website ?? "",
        })
      } else if (!data.extractedData && dealershipId) {
        await updateDealership({
          dealershipId: dealershipId as Id<"dealerships">,
          name: data.organizationName,
          website: data.website,
          dealershipType: data.dealershipType,
        })
      } else if (!data.extractedData && !dealershipId) {
        dealershipId = await createDealership({
          betterAuthOrgId: orgId!,
          extractionData: {
            business: {
              name: data.organizationName,
              type: data.dealershipType || "both",
            },
            identity: {
              logoUrl: typeof data.logo === "string" ? data.logo : undefined,
              primaryColor: data.primaryColor,
            },
            contact: {
              phones: [],
              emails: [],
            },
            location: {
              address: data.stores[0]?.address || {
                street: "",
                city: "",
                state: "",
                zipCode: "",
              },
            },
            online: {
              inventoryUrl: data.stores[0]?.inventoryUrl,
            },
          },
          website: data.website || "",
        })
      }

      // Save owner contact — skip if already eagerly persisted
      if (dealershipId && data.ownerContact && !isResuming) {
        await updateDealership({
          dealershipId: dealershipId as Id<"dealerships">,
          ownerContact: data.ownerContact,
        })
      }

      // Save questionnaire — skip if already eagerly persisted
      if (
        dealershipId &&
        !isResuming &&
        (data.salesFocus || data.biggestHeadache || data.inventoryManagement)
      ) {
        await updateQuestionnaireData({
          dealershipId: dealershipId as Id<"dealerships">,
          questionnaireData: {
            salesFocus: data.salesFocus || undefined,
            biggestHeadache: data.biggestHeadache || undefined,
            inventoryManagement: data.inventoryManagement || undefined,
            dmsSystem: data.dmsSystem || undefined,
            websiteImportance: data.websiteImportance || undefined,
            moneyLeftOnTable: data.moneyLeftOnTable?.length ? data.moneyLeftOnTable : undefined,
            mustHaveIntegrations: data.mustHaveIntegrations?.length
              ? data.mustHaveIntegrations
              : undefined,
            monthlyVolume: data.monthlyVolume || undefined,
          },
        })
      }

      // Create stores — skip stores that already exist in DB
      if (dealershipId) {
        // Ensure member record exists before any permission-gated mutations (e.g. createStore).
        await ensureMembership({
          dealershipId: dealershipId as Id<"dealerships">,
        })

        const existingStoreNames = new Set(
          onboardingState?.stores.map((s: Doc<"stores">) => s.name) ?? [],
        )

        for (const store of data.stores) {
          if (existingStoreNames.has(store.name)) continue

          await createStore({
            dealershipId: dealershipId as Id<"dealerships">,
            name: store.name,
            domain:
              store.domain || `${data.slug}-${store.name.toLowerCase().replace(/\s+/g, "-")}.com`,
            address: store.address,
            phone: store.phone,
            email: store.email,
            inventoryUrl: store.inventoryUrl,
          })
        }

        await setActiveDealership({
          dealershipId: dealershipId as Id<"dealerships">,
        })

        await completeOnboarding({
          dealershipId: dealershipId as Id<"dealerships">,
        })

        await initNotificationPrefs({
          dealershipId: dealershipId as Id<"dealerships">,
          smsOptIn: data.ownerContact?.smsOptIn ?? false,
          smsPhone: data.ownerContact?.phone,
        })
      }

      // Sync owner contact back to auth user so dashboard reflects changes
      if (data.ownerContact?.name) {
        await authClient.updateUser({ name: data.ownerContact.name })
      }
      if (data.ownerContact?.email) {
        const session = await authClient.getSession()
        if (session?.data?.user?.email && session.data.user.email !== data.ownerContact.email) {
          await authClient.changeEmail({ newEmail: data.ownerContact.email })
        }
      }

      void navigate({ to: "/dealerships" })
    } catch (error) {
      console.error("Failed to create organization:", error)
      throw error
    }
  }

  const handleWizardCancel = () => {
    void navigate({ to: "/login", search: { redirect: undefined } })
  }

  // Loading while query resolves
  if (onboardingState === undefined) {
    return (
      <div className="dark">
        <Wallpaper color="blue" className="min-h-svh">
          <div className="flex items-center justify-center min-h-svh">
            <Loader2 className="size-8 animate-spin text-white/60" />
          </div>
        </Wallpaper>
      </div>
    )
  }

  return (
    <div className="dark">
      <Wallpaper color="blue" className="min-h-svh">
        <div className="py-12 px-4">
          <div className="flex justify-center mb-8">
            <BrandMark className="text-white [&_svg]:text-white [&_span]:text-white" />
          </div>
          <OrganizationSetupWizard
            onComplete={handleWizardComplete}
            onCancel={handleWizardCancel}
            initialData={resumeData?.initialData}
            initialStep={resumeData?.initialStep}
            onPersistStep={handlePersistStep}
          />
        </div>
      </Wallpaper>
    </div>
  )
}
